// frontend/src/pages/DataQualityPage.tsx
// v2.0 - DARK PREMIUM REFACTOR

import React, { useMemo, useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, CircularProgress, Alert, Stack,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, FormControl, Select, MenuItem, TextField, AlertTitle,
  Button, SelectChangeEvent
} from '@mui/material';
import { useQueries, useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

// Iconos Dark Premium
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import UpdateIcon from '@mui/icons-material/Update';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import OperationsDetailHeader from '../components/Operations/OperationsDetailHeader';

import {
    DataProfileResponse, ColumnProfileStats, ProcessingStatus, DataType,
    QualityRuleItem, SelectedRules, QualityRulesState, QualityActionOption,
    SaveQualityRulesPayload, SaveQualityRulesResponse
} from '../types';

import {
    getDataProfile, getDataStatus, getQualityRules, saveQualityRules, applyRules 
} from '../services/dataService';
import { SYSTEM_FIELDS } from '../config/systemFields';
import { useMarkOnboardingEvent } from '../modules/pricingJourney/onboarding';
import { useSnackbar } from 'notistack';
import { SkeletonBlock, SkeletonTable } from '../modules/pricingJourney/components/Skeletons';
import MetricCard from '../modules/pricingJourney/components/MetricCard';

// --- ESTILOS DARK PREMIUM ---
const glassCardSx = {
    bgcolor: '#1e293b', 
    border: '1px solid #334155',
    borderRadius: 2,
    p: 3,
    boxShadow: 'none',
    overflow: 'hidden'
};

const stealthTableSx = {
    '& .MuiTableCell-root': {
        borderBottom: '1px solid rgba(51, 65, 85, 0.5)',
        color: '#cbd5e1'
    },
    '& .MuiTableHead-root .MuiTableCell-root': {
        bgcolor: '#0f172a',
        color: '#94a3b8',
        fontWeight: 700,
        fontSize: '0.75rem',
        textTransform: 'uppercase',
        letterSpacing: 1
    },
    '& .MuiTableRow-hover:hover': {
        bgcolor: 'rgba(255, 255, 255, 0.02) !important'
    }
};

const stealthSelectSx = {
    '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
    '& .MuiSelect-select': { 
        bgcolor: 'rgba(255, 255, 255, 0.03)', 
        border: '1px solid #334155',
        borderRadius: 1,
        py: 1,
        fontSize: '0.9rem',
        '&:focus': { bgcolor: 'rgba(255, 255, 255, 0.05)' }
    },
    '& .MuiSelect-icon': { color: '#64748b' }
};

const DATA_TYPES_ARRAY: DataType[] = ['customers', 'products', 'transactions'];

// PRC-032/033: tipo de columna en lenguaje de negocio (no «object», «float64»…).
const dtypeLabel = (dtype?: string | null): string => {
  const value = (dtype ?? '').toLowerCase();
  if (!value) return '—';
  if (value.includes('date') || value.includes('time')) return 'Fecha';
  if (value.includes('bool')) return 'Sí / No';
  if (value.includes('int')) return 'Número entero';
  if (value.includes('float') || value.includes('decimal') || value.includes('num')) return 'Número';
  if (value.includes('object') || value.includes('str') || value.includes('text') || value.includes('category')) return 'Texto';
  return dtype ?? '—';
};

const QUALITY_ACTION_OPTIONS: QualityActionOption[] = [
    { value: 'KEEP', label: 'Mantener la columna' },
    { value: 'DROP_COLUMN', label: 'No usar la columna' },
    { value: 'FILL_NULL_MEAN', label: 'Rellenar vacíos con la media', numericOnly: true },
    { value: 'FILL_NULL_MEDIAN', label: 'Rellenar vacíos con la mediana', numericOnly: true },
    { value: 'FILL_NULL_MODE', label: 'Rellenar vacíos con el valor más frecuente', categoricalOnly: true },
    { value: 'FILL_NULL_VALUE', label: 'Rellenar vacíos con un valor', needsValue: true },
];

// --- StatusInfoBox Refactorizado ---
interface StatusInfoBoxProps { statusData: ProcessingStatus | null | undefined; fileType: DataType; }
const StatusInfoBox = React.memo(({ statusData }: StatusInfoBoxProps): React.ReactNode => {
    const formatDateTime = (isoString?: string): string => { if (!isoString) return 'N/A'; try { const date = new Date(isoString); return date.toLocaleString(undefined, { year: 'numeric', month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' }); } catch (e) { return 'Fecha inválida'; } };
    
    if (!statusData) return null; // Ocultar si no hay datos para limpiar la vista

    const isSuccess = statusData.status === 'success';
    const isError = statusData.status === 'error';
    
    return ( 
        <Alert 
            variant="outlined"
            severity={isSuccess ? 'success' : isError ? 'error' : 'info'} 
            icon={isSuccess ? <CheckCircleOutlineIcon fontSize="inherit" /> : isError ? <ErrorOutlineIcon fontSize="inherit"/> : <UpdateIcon fontSize="inherit"/>} 
            sx={{ 
                mb: 3, 
                bgcolor: isSuccess ? 'rgba(74, 222, 128, 0.05)' : isError ? 'rgba(248, 113, 113, 0.05)' : 'transparent',
                borderColor: isSuccess ? '#4ade80' : isError ? '#f87171' : '#334155',
                color: isSuccess ? '#f8fafc' : isError ? '#fca5a5' : '#94a3b8',
                '& .MuiAlert-icon': { color: isSuccess ? '#4ade80' : isError ? '#f87171' : '#38bdf8' }
            }}
        > 
            <AlertTitle sx={{ fontWeight: 700, fontSize: '0.9rem' }}>
                {isSuccess ? 'Análisis completado' : isError ? 'Error en el análisis' : 'Analizando'}
            </AlertTitle> 
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 0.5 }}>
                <Typography variant="caption" sx={{ opacity: 0.8 }}>
                    Fecha: {formatDateTime(statusData.finished_at || statusData.started_at)}
                </Typography>
                {isSuccess && (
                    <Typography variant="caption" sx={{ opacity: 0.8 }}>
                        Filas: {statusData.rows_processed?.toLocaleString() ?? 'N/A'}
                    </Typography>
                )}
            </Box>
            {isError && <Typography variant="caption" display="block" sx={{ mt: 1 }}>{statusData.error_message}</Typography>}
        </Alert> 
    );
});
StatusInfoBox.displayName = 'StatusInfoBox';

// --- Tipado y Lógica de Queries (MANTENIDO) ---
interface ProcessedQueryResults {
  profileMap: Record<DataType, DataProfileResponse | null | undefined>;
  statusMap: Record<DataType, ProcessingStatus | null | undefined>;
  rulesMap: Record<DataType, QualityRuleItem[] | null | undefined>;
}

const DataQualityPage: React.FC = () => {
  const queryClient = useQueryClient();
  // PRC-019: paso «calidad revisada» de Primeros pasos.
  useMarkOnboardingEvent('quality');

  // Estados Locales
  const [selectedRules, setSelectedRules] = useState<QualityRulesState>({ 
      customers: {}, 
      products: {}, 
      transactions: {}, 
      historical_costs: {} // <--- Dummy para cumplir el tipo
  });
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  // PRC-031: avisos con el estilo común (abajo a la derecha, se cierran solos).
  const { enqueueSnackbar } = useSnackbar();

  // React Query
  const results = useQueries({
    queries: DATA_TYPES_ARRAY.flatMap((dt) => [
      { queryKey: ['dataProfile', dt], queryFn: () => getDataProfile(dt), staleTime: 5 * 60 * 1000, retry: 1 },
      { queryKey: ['dataStatus', dt], queryFn: () => getDataStatus(dt), staleTime: 1 * 60 * 1000 },
      { queryKey: ['qualityRules', dt], queryFn: () => getQualityRules(dt), staleTime: 5 * 60 * 1000, retry: 1 },
    ]),
  });

  // Procesar resultados (Memoización intacta)
  const { profileMap, statusMap, rulesMap } = useMemo((): ProcessedQueryResults => {
    const profiles: Record<DataType, DataProfileResponse | null | undefined> = { 
        customers: undefined, products: undefined, transactions: undefined, historical_costs: undefined 
    };
    const statuses: Record<DataType, ProcessingStatus | null | undefined> = { 
        customers: undefined, products: undefined, transactions: undefined, historical_costs: undefined 
    };
    const rules: Record<DataType, QualityRuleItem[] | null | undefined> = { 
        customers: undefined, products: undefined, transactions: undefined, historical_costs: undefined 
    };
    for (let i = 0; i < results.length; i++) {
      const result = results[i];
      if (!result) continue;
      const dataTypeIndex = Math.floor(i / 3);
      const fileType = DATA_TYPES_ARRAY[dataTypeIndex];
      const queryTypeIndex = i % 3;
      if (result.status === 'success') {
         if (queryTypeIndex === 0) { profiles[fileType] = result.data as any; }
         else if (queryTypeIndex === 1) { statuses[fileType] = result.data as ProcessingStatus | null; }
         else { rules[fileType] = result.data as QualityRuleItem[] | null; }
      } else if (result.status === 'error'){
          if (queryTypeIndex === 0) { profiles[fileType] = null; }
          else if (queryTypeIndex === 1) { statuses[fileType] = null; }
          else { rules[fileType] = null; }
      }
    }
    return { profileMap: profiles, statusMap: statuses, rulesMap: rules };
  }, [results]);

  // --- useEffect para Inicializar Estado Local (MANTENIDO) ---
  useEffect(() => {
    const profilesAvailable = Object.values(profileMap).some(p => p !== undefined);
    const rulesAvailable = Object.values(rulesMap).some(r => r !== undefined);

    if (!isInitialized && profilesAvailable && rulesAvailable) {
      const nextState: QualityRulesState = { 
          customers: {}, products: {}, transactions: {}, historical_costs: {} 
      };

      DATA_TYPES_ARRAY.forEach(dataType => {
        const profile = profileMap[dataType];
        const fetchedRules = rulesMap[dataType];
        const rulesForDt: SelectedRules = {};

        if (profile?.column_profiles) {
          for (const colName of Object.keys(profile.column_profiles)) {
            rulesForDt[colName] = { selected_action: 'KEEP', action_value: null };
          }
        }
        
        if (Array.isArray(fetchedRules)) {
          for (const rule of fetchedRules) {
            if (rule.column_name && rule.selected_action) {
              rulesForDt[rule.column_name] = {
                selected_action: rule.selected_action,
                action_value: rule.action_value,
              };
            }
          }
        }
        nextState[dataType] = rulesForDt;
      });

      setSelectedRules(nextState);
      setIsInitialized(true);
    }
  }, [profileMap, rulesMap, isInitialized]);

  // --- Manejadores para Cambios en Reglas (MANTENIDO) ---
  const handleActionChange = useCallback(( event: SelectChangeEvent<string>, dataType: DataType, columnName: string ): void => {
    const newAction = event.target.value as string; const actionConfig = QUALITY_ACTION_OPTIONS.find(opt => opt.value === newAction);
    setSelectedRules(prev => {
      if (newAction === '') { const ns = { ...(prev[dataType] || {}) }; delete ns[columnName]; return { ...prev, [dataType]: ns }; }
      const prevRule = prev[dataType]?.[columnName] || { selected_action: '', action_value: null }; const nextRule = { ...prevRule, selected_action: newAction, action_value: actionConfig?.needsValue ? prevRule.action_value : null };
      return { ...prev, [dataType]: { ...(prev[dataType] || {}), [columnName]: nextRule } };
    });
  }, []);

  const handleActionValueChange = useCallback(( event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, dataType: DataType, columnName: string ): void => {
    const newValue = event.target.value;
    setSelectedRules(prev => ({ ...prev, [dataType]: { ...(prev[dataType] || {}), [columnName]: { ...(prev[dataType]?.[columnName] || { selected_action: '' }), action_value: newValue } } }));
  }, []);

  // --- Mutaciones (MANTENIDO) ---
  const applyRulesMutation = useMutation< any, Error, { dataType: DataType } >({
      mutationFn: applyRules,
      onSuccess: (data, variables) => {
          console.log(`Rules applied via API for ${variables.dataType}:`, data);
          enqueueSnackbar(data.message || `Reglas guardadas y aplicadas para ${variables.dataType === 'customers' ? 'clientes' : variables.dataType === 'products' ? 'productos' : 'transacciones'}.`, { variant: 'success' });
          queryClient.invalidateQueries({ queryKey: ['dataStatus', variables.dataType] });
      },
      onError: (error, variables) => {
          console.error(`Error applying rules for ${variables.dataType}:`, error);
          if (!saveRulesMutation.isError) {
             enqueueSnackbar(error.message || `Error al aplicar reglas para ${variables.dataType === 'customers' ? 'clientes' : variables.dataType === 'products' ? 'productos' : 'transacciones'}.`, { variant: 'error' });
          }
      },
  });

  const saveRulesMutation = useMutation<SaveQualityRulesResponse, Error, SaveQualityRulesPayload>({
      mutationFn: saveQualityRules,
      onSuccess: (data, variables) => {
          console.log('Rules saved successfully:', data);
          queryClient.invalidateQueries({ queryKey: ['qualityRules', variables.data_type] });
          console.log(`Triggering applyRulesMutation for ${variables.data_type}...`);
          applyRulesMutation.mutate({ dataType: variables.data_type });
      },
      onError: (error, variables) => {
          console.error(`Error saving rules for ${variables.data_type}:`, error);
          enqueueSnackbar(error.message || 'Error desconocido al guardar las reglas.', { variant: 'error' });
      },
  });

  const handleSaveChanges = (dataType: DataType) => {
    let rulesToSave: SelectedRules = {};
    setSelectedRules(currentState => {
        rulesToSave = currentState[dataType] || {};
        return currentState;
    });

    const savePayload: SaveQualityRulesPayload = {
        data_type: dataType,
        rules: rulesToSave || {},
    };
    saveRulesMutation.mutate(savePayload);
  };


  // --- Función para renderizar la tabla (VISUAL UPDATE: DARK PREMIUM) ---
  const renderQualityTable = (dataType: DataType): React.ReactNode => {
    const profile = profileMap[dataType];
    const profileQuery = results[DATA_TYPES_ARRAY.indexOf(dataType) * 3];

    const isSaving = saveRulesMutation.status === 'pending' && saveRulesMutation.variables?.data_type === dataType;
    const isApplying = applyRulesMutation.status === 'pending' && applyRulesMutation.variables?.dataType === dataType;
    const isBusy = isSaving || isApplying;

    if (profileQuery?.status === 'pending') { return <SkeletonTable rows={5} />; }
    if (profileQuery?.status === 'error') { const error = profileQuery.error as AxiosError | Error; const status = (error as AxiosError)?.response?.status; if (status === 404) { return <Alert severity="warning" variant="outlined" sx={{mt:2, borderColor: '#fcd34d', color: '#fcd34d'}}>Aún no hay datos de este tipo. Cárgalos primero desde Carga de datos.</Alert>; } else { return <Alert severity="error" variant="outlined" sx={{mt:2, borderColor: '#f87171', color: '#fca5a5'}}>No se ha podido analizar este archivo: {error.message}</Alert>; } }
    if (!profile || !profile.column_profiles || profile.column_count === 0) { return <Typography sx={{mt: 2, fontStyle: 'italic', color: '#64748b'}}>No hay datos que analizar en este archivo.</Typography>; }

    // Generador de Chips (Dark Mode)
    const getQualitySuggestions = (_colName: string, stats: ColumnProfileStats | null | undefined): React.ReactNode[] => { 
        const suggestions: React.ReactNode[] = []; 
        if (!stats) return [<Chip key="err" label="Sin estadísticas" size="small" sx={{ borderColor: '#f87171', color: '#f87171' }} variant="outlined"/>]; 
        
        const nullPerc = stats.null_percentage ?? 0; 
        if (nullPerc > 80) { suggestions.push(<Chip key="nul-h" label={`Vacíos: ${nullPerc.toFixed(1)} % (muchos)`} size="small" sx={{ borderColor: '#f87171', color: '#f87171' }} variant="outlined"/>); } 
        else if (nullPerc > 20) { suggestions.push(<Chip key="nul-m" label={`Vacíos: ${nullPerc.toFixed(1)} %`} size="small" sx={{ borderColor: '#fcd34d', color: '#fcd34d' }} variant="outlined"/>); } 
        
        if (stats.unique_count === 1 && stats.non_null_count > 1) { suggestions.push(<Chip key="const" label="Siempre el mismo valor" size="small" sx={{ borderColor: '#94a3b8', color: '#94a3b8' }} variant="outlined"/>); } 
        if (stats.unique_count && stats.total_count > 10 && (stats.unique_count / stats.total_count > 0.98) && !stats.dtype?.includes('date')) { suggestions.push(<Chip key="card-h" label="Parece un identificador" size="small" sx={{ borderColor: '#38bdf8', color: '#38bdf8' }} variant="outlined"/>); } 
        if (stats.unique_count && stats.unique_count <= 7 && (!stats.dtype?.includes('float'))) { suggestions.push(<Chip key="card-l" label="Pocas categorías" size="small" sx={{ borderColor: '#a78bfa', color: '#a78bfa' }} variant="outlined"/>); } 
        
        if (suggestions.length === 0 && stats.non_null_count > 0) { suggestions.push(<Chip key="ok" label="Sin incidencias" size="small" sx={{ borderColor: '#4ade80', color: '#4ade80' }} variant="outlined"/>); } 
        else if (suggestions.length === 0 && stats.non_null_count === 0) { suggestions.push(<Chip key="empty" label="Columna vacía" size="small" sx={{ borderColor: '#f87171', color: '#f87171' }} variant="outlined"/>); } 
        return suggestions; 
    };

    const validColumns = Object.entries(profile.column_profiles).filter(([colName]) => !['id', 'user_id', 'created_at', 'updated_at'].includes(colName));
    if (validColumns.length === 0) { return <Typography sx={{mt: 2, fontStyle: 'italic', color: '#64748b'}}>Sin columnas relevantes.</Typography>; }

    return (
      <>
        <TableContainer sx={{ mt: 2, overflowX: 'auto', ...stealthTableSx }}>
          <Table stickyHeader size="small" sx={{ tableLayout: 'fixed' }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: '20%' }}>Columna</TableCell> 
                <TableCell sx={{ width: '10%' }}>Tipo</TableCell> 
                <TableCell align="right" sx={{ width: '10%' }}>% vacíos</TableCell>
                <TableCell align="right" sx={{ width: '10%' }}>Únicos</TableCell> 
                <TableCell sx={{ width: '20%' }}>Revisión automática</TableCell>
                <TableCell sx={{ width: '20%' }}>Acción</TableCell> 
                <TableCell sx={{ width: '10%' }}>Valor</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {validColumns.map(([colName, stats]) => {
                  if (!stats) return null;
                  
                  // Lógica de Fortificación (MANTENIDA)
                  const systemFieldDef = SYSTEM_FIELDS[dataType]?.find(f => f.key === colName);
                  const isRequired = systemFieldDef?.required || false;

                  let filteredActions = [...QUALITY_ACTION_OPTIONS];
                  if (isRequired) {
                      filteredActions = filteredActions.filter(opt => opt.value !== 'DROP_COLUMN');
                  }
                  
                  const availableActions = filteredActions.filter(opt => {
                      const dtype = stats?.dtype?.toLowerCase() || '';
                      if (opt.numericOnly && !dtype.includes('float') && !dtype.includes('int')) return false;
                      if (opt.categoricalOnly && (dtype.includes('float') || dtype.includes('int'))) return false;
                      return true;
                  });

                  const suggestions = getQualitySuggestions(colName, stats);
                  const currentRule = selectedRules[dataType]?.[colName];
                  const selectedActionValue = currentRule?.selected_action || '';
                  const actionValueInput = currentRule?.action_value ?? '';
                  const actionConfig = availableActions.find(opt => opt.value === selectedActionValue);
                  const needsValue = actionConfig?.needsValue || false;
                  
                  return (
                    <TableRow key={`${dataType}-${colName}`}>
                      <TableCell component="th" scope="row" sx={{ fontWeight: 'medium', whiteSpace: 'nowrap', color: '#f8fafc' }}>
                        {colName} {isRequired && <span style={{ color: '#f87171', fontWeight: 'bold' }}>*</span>}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{dtypeLabel(stats?.dtype)}</TableCell>
                      <TableCell align="right" sx={{ color: (stats?.null_percentage ?? 0) > 20 ? '#f87171' : 'inherit', fontWeight: (stats?.null_percentage ?? 0) > 20 ? 'bold' : 'normal' }}> 
                        {stats?.null_percentage?.toFixed(1) ?? 'N/A'}% 
                      </TableCell>
                      <TableCell align="right">{stats?.unique_count?.toLocaleString() ?? 'N/A'}</TableCell>
                      <TableCell sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}> {suggestions} </TableCell>
                      <TableCell>
                        <FormControl fullWidth size="small" variant="outlined" disabled={isBusy} sx={stealthSelectSx}>
                           <Select value={selectedActionValue} onChange={(e) => handleActionChange(e, dataType, colName)} displayEmpty>
                             {availableActions.map(action => ( <MenuItem key={action.value} value={action.value}>{action.label}</MenuItem> ))}
                           </Select>
                         </FormControl>
                      </TableCell>
                      <TableCell> 
                        {needsValue && ( 
                            <TextField 
                                fullWidth size="small" variant="outlined" placeholder="Valor..." 
                                value={actionValueInput} onChange={(e) => handleActionValueChange(e, dataType, colName)} disabled={isBusy} 
                                sx={{ 
                                    '& .MuiOutlinedInput-root': { color: '#cbd5e1', '& fieldset': { borderColor: '#334155' } },
                                    input: { py: 1, fontSize: '0.9rem' }
                                }} 
                            /> 
                        )} 
                      </TableCell>
                    </TableRow>
                  );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
             <Button 
                variant="contained" 
                size="medium" 
                startIcon={isBusy ? <CircularProgress size={16} color="inherit" /> : <AutoFixHighIcon />} 
                onClick={() => handleSaveChanges(dataType)} 
                disabled={isBusy}
                sx={{ 
                    bgcolor: '#38bdf8', color: '#0f172a', fontWeight: 700, 
                    '&:hover': { bgcolor: '#0ea5e9' } 
                }}
            >
                {isSaving ? 'Guardando…' : isApplying ? 'Aplicando…' : `Aplicar reglas a ${dataType === 'customers' ? 'clientes' : dataType === 'products' ? 'productos' : dataType === 'transactions' ? 'transacciones' : 'costes históricos'}`}
            </Button>
        </Box>
      </>
    );
  };

  // --- Renderizado Principal ---
  return (
    <Box sx={{ width: '100%', px: { xs: 2, sm: 3, lg: 4 }, pt: 3, pb: 7, minHeight: '100vh' }}>
      <OperationsDetailHeader
        eyebrow="Operaciones · Calidad"
        title="Calidad y normalización"
        description="Primero un resumen de cada archivo; abajo, el detalle por columna y las reglas de limpieza si necesitas intervenir."
      />

      {/* PRC-033: resumen por archivo antes del detalle. */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0,1fr))' }, gap: 2, mb: 4 }}>
        {DATA_TYPES_ARRAY.map((dt) => {
          const profileQuery = results[DATA_TYPES_ARRAY.indexOf(dt) * 3];
          const profile = profileMap[dt];
          const status = statusMap[dt];
          const title = dt === 'customers' ? 'Clientes' : dt === 'products' ? 'Productos' : 'Transacciones';
          if (profileQuery?.status === 'pending') return <SkeletonBlock key={dt} h={132} />;
          const columns = profile?.column_profiles
            ? Object.entries(profile.column_profiles).filter(([name]) => !['id', 'user_id', 'created_at', 'updated_at'].includes(name))
            : [];
          const toReview = columns.filter(([, stats]) => !stats || stats.non_null_count === 0 || (stats.null_percentage ?? 0) > 20).length;
          const hasData = columns.length > 0;
          return (
            <MetricCard
              key={dt}
              label={title}
              value={hasData ? `${(profile?.row_count ?? 0).toLocaleString('es-ES')} filas` : 'Sin datos'}
              change={hasData ? (toReview > 0 ? `${toReview} de ${columns.length} columnas a revisar` : `${columns.length} columnas sin incidencias`) : undefined}
              changeTone={toReview > 0 ? 'down' : 'up'}
              note={status?.status === 'error'
                ? 'El último análisis dio error'
                : hasData
                  ? (
                    <Box component="a" href={`#calidad-${dt}`} sx={{ color: '#7dd3fc', textDecoration: 'none', fontWeight: 700 }}>Ver detalle</Box>
                  )
                  : 'Carga este archivo desde Carga de datos'}
            />
          );
        })}
      </Box>

      {/* --- STACK DE SECCIONES (Clientes, Productos, etc.) --- */}
      <Stack spacing={4}>
          {DATA_TYPES_ARRAY
              .map((dt) => {
                const profileQuery = results[DATA_TYPES_ARRAY.indexOf(dt) * 3];
                const statusQuery = results[DATA_TYPES_ARRAY.indexOf(dt) * 3 + 1];
                const isSectionLoading = profileQuery?.status === 'pending' || statusQuery?.status === 'pending';
                const sectionTitle = dt === 'customers' ? 'Clientes' : dt === 'products' ? 'Productos' : dt === 'transactions' ? 'Transacciones' : 'Costes históricos';

             return (
                 <Paper key={dt} id={`calidad-${dt}`} elevation={0} sx={{ ...glassCardSx, scrollMarginTop: 16 }}>
                   {/* Header de Sección */}
                   <Box sx={{ mb: 2, pb: 1, borderBottom: '1px solid rgba(51, 65, 85, 0.5)' }}>
                       <Typography variant="h6" component="h2" sx={{ color: '#f8fafc', fontWeight: 600 }}>
                          {sectionTitle}
                       </Typography>
                   </Box>

                   {/* Estado de Carga / Tabla */}
                   {statusQuery?.status !== 'pending' && <StatusInfoBox statusData={statusMap[dt]} fileType={dt} />}
                   {isSectionLoading && <SkeletonTable rows={5} />}
                   {!isSectionLoading && renderQualityTable(dt)}
                 </Paper>
             );
           })}
        </Stack>

    </Box>
  );
};

export default DataQualityPage;
